import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import api, { API, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { chf } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader } from "@/components/Common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MapPin, Star, BadgeCheck, ArrowLeft, Heart, MessageSquare } from "lucide-react";

export default function VendorProfile() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user, wedding } = useAuth();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [rfq, setRfq] = useState({ service: "", description: "", guest_count: "", budget_range: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => { axios.get(`${API}/marketplace/vendors/${slug}`).then(({ data }) => setD(data)).catch(() => setD(false)); }, [slug]);

  if (d === null) return <div className="min-h-screen bg-background"><Loader /></div>;
  if (d === false) return <div className="min-h-screen flex items-center justify-center">Prestataire introuvable</div>;
  const v = d.vendor;

  const requestQuote = async () => {
    if (!user) { nav("/login"); return; }
    if (!wedding?.wedding) { toast.error("Créez d'abord votre mariage."); return; }
    setSending(true);
    try {
      const source = window.location.pathname.startsWith("/prestataires") ? "direct_vendor_profile" : "marketplace_search";
      await api.post(`/weddings/${wedding.wedding.id}/quote-requests`, {
        vendor_id: v.id, service: rfq.service, description: rfq.description, source,
        guest_count: rfq.guest_count ? +rfq.guest_count : null, budget_range: rfq.budget_range,
      });
      toast.success("Demande de devis envoyée !");
      setOpen(false); nav("/app/messages");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const toggleFav = async () => {
    if (!wedding?.wedding) { toast.error("Créez d'abord votre mariage."); return; }
    const { data } = await api.post(`/weddings/${wedding.wedding.id}/favorites/${v.id}`);
    toast.success(data.favorited ? "Ajouté à vos favoris" : "Retiré des favoris");
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="relative h-72 lg:h-96">
        <img src={v.cover_image || "https://images.unsplash.com/photo-1519741497674-611481863552?w=1200&q=80"} alt={v.business_name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/70 to-transparent" />
        <Link to="/marketplace" className="absolute top-6 left-6 text-white flex items-center gap-2 text-sm">
          <ArrowLeft className="w-4 h-4" /> Marketplace</Link>
        <div className="absolute bottom-8 left-0 right-0">
          <div className="max-w-5xl mx-auto px-6 text-white">
            <div className="flex items-center gap-2">
              <h1 className="font-serif text-4xl lg:text-5xl">{v.business_name}</h1>
              {v.verified && <BadgeCheck className="w-6 h-6 text-champagne" />}</div>
            <div className="flex items-center gap-4 mt-2 opacity-90">
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {v.city}, {v.canton}</span>
              <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-champagne text-champagne" /> {v.rating} ({v.review_count} avis)</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <div><div className="eyebrow mb-2">À propos</div>
            <p className="text-stone-700 leading-relaxed">{v.description}</p></div>
          <div><div className="eyebrow mb-3">Forfaits</div>
            <div className="space-y-3">
              {d.packages.map((p) => (
                <div key={p.id} className="card-lux p-5 flex items-center justify-between">
                  <div><div className="font-serif text-lg">{p.name}</div>
                    <div className="text-sm text-stone-500">{p.description}</div></div>
                  <div className="font-mono text-lg">{chf(p.price)}</div></div>
              ))}
            </div></div>
          {d.reviews.length > 0 && (
            <div><div className="eyebrow mb-3">Avis vérifiés</div>
              <div className="space-y-3">
                {d.reviews.map((r) => (
                  <div key={r.id} className="card-lux p-5">
                    <div className="flex items-center gap-1 mb-1">
                      {[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${i < r.rating ? "fill-champagne text-champagne" : "text-stone-200"}`} />)}</div>
                    <p className="text-sm text-stone-700">{r.text}</p>
                    <div className="text-xs text-stone-400 mt-1">{r.author}</div></div>
                ))}
              </div></div>
          )}
        </div>
        <div className="lg:sticky lg:top-6 h-fit">
          <div className="card-lux p-6">
            <div className="text-sm text-stone-500">À partir de</div>
            <div className="font-mono text-3xl text-stone-900">{chf(v.starting_price)}</div>
            <Button data-testid="request-quote-btn" onClick={() => setOpen(true)} className="w-full rounded-full mt-5 h-11">
              <MessageSquare className="w-4 h-4 mr-1" /> Demander un devis</Button>
            <Button onClick={toggleFav} variant="outline" className="w-full rounded-full mt-3 h-11">
              <Heart className="w-4 h-4 mr-1" /> Enregistrer</Button>
            {(v.email || v.phone || v.website) && (
              <div className="mt-4 pt-4 border-t space-y-1.5 text-sm" data-testid="vendor-contact">
                <div className="text-xs uppercase tracking-wide text-stone-400 mb-1">Coordonnées</div>
                {v.phone && <a href={`tel:${v.phone}`} className="block text-stone-600 hover:text-stone-900">{v.phone}</a>}
                {v.email && <a href={`mailto:${v.email}`} className="block text-stone-600 hover:text-stone-900">{v.email}</a>}
                {v.website && <a href={v.website} target="_blank" rel="noreferrer" className="block text-champagne underline">Site web</a>}
              </div>
            )}
            <p className="text-[11px] text-stone-400 mt-4">Le contrat et le paiement se règlent directement entre vous et le prestataire. La plateforme ne prélève aucune commission.</p>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>Demande de devis — {v.business_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Prestation souhaitée</Label><Input value={rfq.service} onChange={(e) => setRfq({ ...rfq, service: e.target.value })} className="mt-1 h-11" placeholder="Photographe journée complète" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nombre d'invités</Label><Input type="number" value={rfq.guest_count} onChange={(e) => setRfq({ ...rfq, guest_count: e.target.value })} className="mt-1 h-11" /></div>
              <div><Label>Budget indicatif</Label><Input value={rfq.budget_range} onChange={(e) => setRfq({ ...rfq, budget_range: e.target.value })} className="mt-1 h-11" placeholder="3000-4000 CHF" /></div>
            </div>
            <div><Label>Message</Label><Textarea value={rfq.description} onChange={(e) => setRfq({ ...rfq, description: e.target.value })} className="mt-1" rows={3} /></div>
          </div>
          <DialogFooter><Button data-testid="send-rfq" onClick={requestQuote} disabled={sending} className="rounded-full">
            {sending ? "Envoi…" : "Envoyer la demande"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
